<template>
  <div class="admin-container">
    <!-- Loading Overlay -->
    <LoadingOverlay
      v-model="isInitialLoading"
      message="Loading activity data..."
      subtitle="Please wait while we fetch your lesson materials"
      v-bind="loadingOverlayProps"
    />

    <div class="admin-header">
      <h1>Edit Lesson Materials</h1>
      <div class="action-buttons">
        <button
          class="preview-button"
          @click="showPreviewModal"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'preview'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          {{
            isSubmitting && savingType === "preview"
              ? "Saving & Previewing..."
              : "Preview"
          }}
        </button>
        <button
          class="draft-button"
          @click="saveActivity('draft')"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'draft'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
            ></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          {{
            isSubmitting && savingType === "draft"
              ? "Saving..."
              : "Save as Draft"
          }}
        </button>
        <button
          class="publish-button"
          @click="saveActivity('published')"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'published'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
            ></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          {{
            isSubmitting && savingType === "published"
              ? "Publishing..."
              : "Save as Published"
          }}
        </button>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="showValidationErrors" class="validation-errors">
      <div class="error-header">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h3>Please fix the following errors:</h3>
        <button class="close-error" @click="showValidationErrors = false">
          ×
        </button>
      </div>
      <ul>
        <li v-for="(error, field) in validationErrors" :key="field">
          {{ error }}
        </li>
      </ul>
    </div>

    <!-- Preview Confirmation Modal -->
    <ConfirmationModal
      v-model="showPreviewConfirm"
      title="Save and Preview Activity"
      message="This will save your current changes and open the activity in preview mode. Do you want to continue?"
      confirm-text="Save & Preview"
      cancel-text="Cancel"
      :loading="isSubmitting && savingType === 'preview'"
      @confirm="confirmPreview"
      @cancel="closePreviewModal"
    />

    <div class="admin-content-layout">
      <div class="admin-main-content">
        <TitleSection
          ref="titleSectionRef"
          @update:title="updateTitle"
          @update:subtitle="updateSubtitle"
          @update:duration="updateDuration"
        />
        <LessonMaterialsEditor
          ref="editorRef"
          @tabs-updated="updateTabs"
          :is-edit-form="true"
        />
      </div>
      <div
        class="admin-right-sidebar"
        :class="{ 'mobile-open': rightSidebarOpen }"
      >
        <div class="admin-sidebar-header">
          <button
            class="close-sidebar-button"
            @click="toggleRightSidebar"
            v-if="isMobile"
          >
            Close Sidebar
          </button>
        </div>
        <div class="admin-sidebar-content">
          <!-- Sidebar content here -->
          <RightSidebar
            ref="rightSidebarRef"
            @cover-image-updated="updateCoverImage"
            @tag-sections-updated="updateTagSections"
            @category-sections-updated="updateCategorySections"
            @permalink-updated="updatePermalink"
            @grade-updated="updateGrade"
            @focus-updated="updateFocus"
            @airtable-id-updated="updateAirtableId"
            @subject-updated="updateSubject"
            @school-updated="updateSchool"
            @visibility-updated="updateVisibility"
            @locale-updated="updateLocale"
          />
        </div>
      </div>
    </div>

    <!-- Snackbar Notification -->
    <div v-if="notification.show" class="snackbar" :class="notification.type">
      <div class="snackbar-content">
        <svg
          v-if="notification.type === 'success'"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ notification.message }}</span>
      </div>
      <button class="snackbar-close" @click="closeNotification">×</button>
    </div>

    <button
      class="floating-button"
      @click="toggleRightSidebar"
      v-if="isMobile && !rightSidebarOpen"
    >
      Open Sidebar
    </button>
    <div
      class="right-sidebar-overlay"
      v-if="rightSidebarOpen && isMobile"
      @click="toggleRightSidebar"
    />
  </div>
</template>

<script setup>
/* eslint-disable */
import TitleSection from "@/components/admin/lesson-plans/TitleSection.vue";
import RightSidebar from "@/components/admin/lesson-materials/RightSidebar.vue";
import ConfirmationModal from "@/components/common/ConfirmationModal.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import makercartActivityService from "@/services/makercartActivityService";
import { useUnsavedChanges } from "@/composeables/useUnsavedChanges";
import { useAdminLoadingOverlay } from "@/composeables/useAdminLayout";
import { inject, onMounted, onUnmounted, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import LessonMaterialsEditor from "@/components/admin/lesson-materials/LessonMaterialsEditor.vue";

const router = useRouter();
const route = useRoute();
const rightSidebarOpen = ref(false);
const isMobile = ref(window.innerWidth < 991);
const editorRef = ref(null);
const rightSidebarRef = ref(null);
const titleSectionRef = ref(null);
const isSubmitting = ref(false);
const savingType = ref(null); // Tracks which save operation is in progress
const validationErrors = ref({});
const showValidationErrors = ref(false);
const showPreviewConfirm = ref(false);
const isInitialLoading = ref(true);

const notify = inject("notify");

// Admin layout integration
const { loadingOverlayProps } = useAdminLoadingOverlay();

// Activity data
const activityData = ref({
  id: route.params.id,
  title: "",
  subtitle: "",
  duration: "",
  coverImage: null,
  permalink: "",
  tagSections: [],
  categorySections: [],
  tabs: [],
  materials: [],
  grade: "",
  focus: "",
  airtable_id: "",
  subject: "",
  school: [],
  school_names: [],
  visibility: "",
  slug: "",
  slideUrl: "",
  slidesCount: 0,
  updatedAt: null,
  educator_guide_materials: [],
  material_provided: [],
  maker_approach: "",
  status: "",
  locale: [],
});

// Initialize unsaved changes tracking
const {
  markAsChanged,
  markAsSaved,
  setInitialLoading,
  incrementPendingOperations,
  decrementPendingOperations,
} = useUnsavedChanges(activityData, {
  usePendingOperations: true,
  confirmMessage:
    "You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.",
});

// Notification system
const notification = ref({
  show: false,
  message: "",
  type: "success", // success or error
  timeout: null,
});

const showNotification = (message, type = "success") => {
  // Clear any existing timeout
  if (notification.value.timeout) {
    clearTimeout(notification.value.timeout);
  }

  // Set notification properties
  notification.value.show = true;
  notification.value.message = message;
  notification.value.type = type;

  // Auto-close after 5 seconds
  notification.value.timeout = setTimeout(() => {
    closeNotification();
  }, 5000);
};

const closeNotification = () => {
  notification.value.show = false;
  if (notification.value.timeout) {
    clearTimeout(notification.value.timeout);
  }
};

// Update methods for two-way binding
const updateTitle = (title) => {
  activityData.value.title = title;
  markAsChanged();
};

const updateSubtitle = (subtitle) => {
  activityData.value.subtitle = subtitle;
  markAsChanged();
};

const updateDuration = (duration) => {
  activityData.value.duration = duration;
  markAsChanged();
};

const updateCoverImage = (coverImage) => {
  activityData.value.coverImage = coverImage;
  markAsChanged();
};

const updatePermalink = (permalink) => {
  activityData.value.permalink = permalink;
  markAsChanged();
};

const updateTagSections = (tagSections) => {
  activityData.value.tagSections = tagSections;
  markAsChanged();
};

const updateCategorySections = (categorySections) => {
  activityData.value.categorySections = categorySections;
  markAsChanged();
};

const deletedMaterialIds = ref([]);

const cloneMaterials = (materials = []) =>
  materials.map((material) => (material ? { ...material } : undefined));

const getMaterialId = (material) => material?.id ?? null;

const trackDeletedMaterials = (previousMaterials, nextMaterials) => {
  const previousIds = (previousMaterials || [])
    .map(getMaterialId)
    .filter((id) => id != null);
  const currentIds = new Set(
    (nextMaterials || []).map(getMaterialId).filter((id) => id != null)
  );

  previousIds.forEach((id) => {
    if (!currentIds.has(id) && !deletedMaterialIds.value.includes(id)) {
      deletedMaterialIds.value.push(id);
    }
  });

  deletedMaterialIds.value = deletedMaterialIds.value.filter(
    (id) => !currentIds.has(id)
  );
};

const updateTabs = (tabs, materials) => {
  activityData.value.tabs = tabs;

  // Update instructional materials if provided
  if (materials && Array.isArray(materials)) {
    // Clone so editor mutations don't share the same array reference
    trackDeletedMaterials(activityData.value.materials, materials);
    activityData.value.materials = cloneMaterials(materials);
  }
  markAsChanged();
};

const updateGrade = (grade) => {
  // Don't overwrite existing grade with empty value unless it's intentional
  if (!grade && activityData.value.grade) {
    return;
  }

  activityData.value.grade = grade;
  markAsChanged();
};

const updateFocus = (focus) => {
  activityData.value.focus = focus;
  markAsChanged();
};

const updateAirtableId = (airtableId) => {
  activityData.value.airtable_id = airtableId;
  markAsChanged();
};

const updateSubject = (subject) => {
  activityData.value.subject = subject;
  markAsChanged();
};

const updateSchool = (school) => {
  activityData.value.school = school;
  markAsChanged();
};

const updateVisibility = (visibility) => {
  activityData.value.visibility = visibility;
  markAsChanged();
};

const updateLocale = (loc) => {
  activityData.value.locale = loc || [];
  markAsChanged();
};

const handleResize = () => {
  isMobile.value = window.innerWidth < 991;
};

// Fetch activity data from API
const fetchActivityData = async () => {
  try {
    const response = await makercartActivityService.getEducatorGuideById(
      route.params.id
    );
    if (response.status === 200) {
      const data = response.data.data;

      // Update basic activity data
      activityData.value.id = data.id;
      activityData.value.title = data.title;
      activityData.value.subtitle = data.short_description || "";
      activityData.value.duration = data.duration || "";
      activityData.value.slug = data.slug;
      activityData.value.permalink = data.permalink;
      activityData.value.coverImage = data.thumbnail;
      activityData.value.slideUrl = data.slide_url || "";
      activityData.value.grade = data.grade || "";
      activityData.value.focus = data.focus || "";
      activityData.value.airtable_id = data.airtable_id || "";
      activityData.value.subject = data.subject || "";
      activityData.value.updatedAt =
        data.updated_at || data.date || new Date().toLocaleString();
      activityData.value.educator_guide_materials =
        data.educator_guide_instructional || [];
      activityData.value.material_provided = data.material_provided || [];
      activityData.value.maker_approach = data.maker_approach || "";
      activityData.value.status = data.status || "";
      activityData.value.public_visibility = data.public_visibility || false;

      // Parse locale/locales
      let parsedLocales = [];
      if (data.locales) {
        if (typeof data.locales === "string") {
          try {
            parsedLocales = JSON.parse(data.locales);
          } catch (e) {
            parsedLocales = [];
          }
        } else if (Array.isArray(data.locales)) {
          parsedLocales = data.locales;
        }
      } else if (data.locale) {
        if (typeof data.locale === "string") {
          parsedLocales = data.locale.split(",").map((l) => l.trim());
        } else if (Array.isArray(data.locale)) {
          parsedLocales = data.locale;
        }
      }
      activityData.value.locale = parsedLocales;

      // Process schools data - store objects { name, country }
      if (data.schools && Array.isArray(data.schools)) {
        activityData.value.school = data.schools.map((school) => ({
          title: school.name || school.title,
          country: school.country || school.location || "",
        }));
      } else {
        activityData.value.school = [];
      }

      // Set visibility based on public_visibility
      activityData.value.visibility = data.public_visibility
        ? "public_preview"
        : "restricted_view";

      // Try to get slide count from metadata if available
      if (data.meta && data.meta.slides_count) {
        activityData.value.slidesCount = parseInt(data.meta.slides_count);
      } else {
        activityData.value.slidesCount = 0;
      }

      // Parse content JSON if available
      if (data.content) {
        try {
          activityData.value.tabs = JSON.parse(data.content);
        } catch (e) {
          console.error("Error parsing activity content:", e);
          activityData.value.tabs = [];
        }
      }

      // Process categories into tag sections (school) and category sections (non-school)
      if (data.categories && data.categories.length > 0) {
        // ... (rest of category processing)
      }

      // Update child components
      incrementPendingOperations();
      updateChildComponents();

      // Mark as saved state after loading data and child component updates
      setTimeout(() => {
        markAsSaved();
        setTimeout(() => {
          decrementPendingOperations();
          setInitialLoading(false);
          isInitialLoading.value = false;
        }, 500);
      }, 500);
    }
  } catch (error) {
    console.error("Error fetching activity data:", error);
    showNotification(
      "Failed to load activity data. Please try again.",
      "error"
    );
    setInitialLoading(false);
    isInitialLoading.value = false;
  }
};

// Update all child components with the current data
const updateChildComponents = () => {
  if (titleSectionRef.value) {
    titleSectionRef.value.updateFromParent(
      activityData.value.title,
      activityData.value.subtitle,
      activityData.value.duration
    );
  }

  if (editorRef.value) {
    editorRef.value.updateFromParent(
      activityData.value.tabs,
      activityData.value.materials
    );

    if (activityData.value.slideUrl) {
      setTimeout(() => {
        editorRef.value.setSlideAttachedState({
          url: activityData.value.slideUrl,
          code: null,
        });
      }, 300);
    }

    if (
      activityData.value.educator_guide_materials &&
      activityData.value.educator_guide_materials.length > 0
    ) {
      const materials = activityData.value.educator_guide_materials.map(
        (material) => ({
          id: material.id,
          title: material.name,
          url: material.url,
        })
      );
      // Separate clones so remove mutations in the editor don't wipe parent state
      activityData.value.materials = cloneMaterials(materials);
      deletedMaterialIds.value = [];
      editorRef.value.setInstructionalMaterials(cloneMaterials(materials));
    }
  }

  if (rightSidebarRef.value) {
    const editFormData = {
      airtable_id: activityData.value.airtable_id,
      focus: activityData.value.focus,
      subject: activityData.value.subject,
      maker_approach: activityData.value.maker_approach,
      material_provided: activityData.value.material_provided,
      status: activityData.value.status,
      grade: activityData.value.grade,
      school_objects: activityData.value.school,
      public_visibility: activityData.value.public_visibility,
      locale: activityData.value.locale,
    };

    rightSidebarRef.value.updateFromParent(
      activityData.value.coverImage,
      activityData.value.tagSections,
      activityData.value.permalink || activityData.value.slug,
      null,
      editFormData,
      activityData.value.categorySections,
      activityData.value.locale
    );
  }
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
  setInitialLoading(true);
  if (route.params.id) {
    fetchActivityData();
  } else {
    setInitialLoading(false);
    isInitialLoading.value = false;
    router.push({ name: "MakercartActivitiesListPage" });
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

function toggleRightSidebar() {
  rightSidebarOpen.value = !rightSidebarOpen.value;
}

const showPreviewModal = () => {
  showPreviewConfirm.value = true;
};

const closePreviewModal = () => {
  showPreviewConfirm.value = false;
};

const confirmPreview = async () => {
  showPreviewConfirm.value = false;
  await previewActivity();
};

// Shared function to prepare activity data for API
const prepareActivityData = async (status = null) => {
  const formData = new FormData();
  formData.append("title", activityData.value.title);
  // ... (other fields)
  formData.append(
    "slug",
    activityData.value.permalink || activityData.value.slug
  );
  formData.append("content", JSON.stringify(activityData.value.tabs));
  formData.append("short_description", activityData.value.subtitle);
  formData.append("duration", activityData.value.duration);

  const finalStatus = status || activityData.value.status || "draft";
  formData.append("status", finalStatus);

  formData.append("permalink", activityData.value.permalink);
  formData.append("grade", activityData.value.grade);
  formData.append("focus", activityData.value.focus);
  formData.append("airtable_id", activityData.value.airtable_id);
  formData.append("subject", activityData.value.subject);

  let localePayload = "";
  if (
    Array.isArray(activityData.value.locale) &&
    activityData.value.locale.length > 0
  ) {
    localePayload = activityData.value.locale.join(", ");
  } else if (
    typeof activityData.value.locale === "string" &&
    activityData.value.locale.length > 0
  ) {
    localePayload = activityData.value.locale;
  }
  formData.append("locale", localePayload);

  // Add school_objects (JSON string of array of objects)
  if (activityData.value.school && activityData.value.school.length > 0) {
    formData.append(
      "school_objects",
      JSON.stringify(
        activityData.value.school.map((s) => ({
          title: s.title || s.name,
          country: s.country || "",
        }))
      )
    );
  } else {
    formData.append("school_objects", JSON.stringify([]));
  }

  // Add public_visibility if visibility is "public_preview"
  if (activityData.value.visibility === "public_preview") {
    formData.append("public_visibility", "true");
  }

  let slideData = null;
  // Add slide URL if slides are attached or if it existed before
  if (
    (editorRef.value && editorRef.value.isSlideAttached()) ||
    activityData.value.slideUrl
  ) {
    slideData = editorRef.value.getSlideData();
    let slideUrl = activityData.value.slideUrl;

    if (slideData) {
      slideUrl = slideData.url;
    }

    if (slideUrl) {
      formData.append("slide_url", slideUrl);
    }
  }

  formData.append(
    "date",
    new Date()
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-")
  );

  // Process categories from both tagSections and categorySections
  const allCategories = [
    ...formatCategories(activityData.value.tagSections),
    ...formatCategories(activityData.value.categorySections),
  ];
  if (allCategories.length > 0) {
    formData.append("categories", JSON.stringify(allCategories));
  } else {
    formData.append("categories", "");
  }

  // Process cover image
  if (activityData.value.coverImage) {
    // If the image is a base64 string, convert it to a file
    if (
      typeof activityData.value.coverImage === "string" &&
      activityData.value.coverImage.startsWith("data:image")
    ) {
      const imageBlob = await fetch(activityData.value.coverImage).then((r) =>
        r.blob()
      );
      const imageFile = new File([imageBlob], `cover-image-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      formData.append("thumbnail", imageFile);
    } else if (typeof activityData.value.coverImage === "object") {
      // If it's already a file object, just append it
      formData.append("thumbnail", activityData.value.coverImage);
    }
  }

  return { formData, slideData };
};

// Shared function to save activity data
const saveActivityData = async (formData, slideData) => {
  let response;

  // Check if this is a new activity or updating existing one
  if (activityData.value.id) {
    // Update existing activity
    response = await makercartActivityService.updateEducatorGuide(
      activityData.value.id,
      formData
    );
  } else {
    // Create new activity
    response = await makercartActivityService.createEducatorGuide(formData);
    // Update the activity ID for future operations
    if (response.data && response.data.data && response.data.data.id) {
      activityData.value.id = response.data.data.id;
    }
  }

  // Handle slide assignment if needed
  if (slideData && slideData.code && activityData.value.id) {
    await makercartActivityService.assignSlides({
      educator_guide_id: activityData.value.id,
      code: slideData.code,
      replace_existing: true,
    });
  }

  return response;
};

const deleteRemovedMaterials = async () => {
  const currentMaterials =
    editorRef.value?.getInstructionalMaterials?.() ||
    activityData.value.materials ||
    [];
  const currentIds = new Set(
    currentMaterials.map(getMaterialId).filter((id) => id != null)
  );

  const originalIds = (activityData.value.educator_guide_materials || [])
    .map(getMaterialId)
    .filter((id) => id != null);

  const idsToDelete = [
    ...new Set([
      ...originalIds.filter((id) => !currentIds.has(id)),
      ...deletedMaterialIds.value.filter((id) => !currentIds.has(id)),
    ]),
  ];

  if (idsToDelete.length === 0) return;

  await Promise.all(
    idsToDelete.map((id) => makercartActivityService.deleteMaterials(id))
  );
  deletedMaterialIds.value = [];
};

// Preview activity function (refactored)
async function previewActivity() {
  if (isSubmitting.value) return;

  try {
    // Validate data first
    if (!validateActivityData()) {
      return;
    }

    isSubmitting.value = true;
    savingType.value = "preview";
    validationErrors.value = {};
    showValidationErrors.value = false;

    // Prepare and save data (preserving original status)
    const { formData, slideData } = await prepareActivityData();
    await saveActivityData(formData, slideData);
    await deleteRemovedMaterials();

    notify.success(
      "Activity saved successfully! Redirecting to preview...",
      "Success"
    );

    // Mark as saved after successful save
    markAsSaved();

    // Small delay to show the success message before redirecting
    setTimeout(() => {
      // Navigate to preview page with activity ID
      router.push({
        name: "LessonMaterialsPreviewPage",
        params: { id: activityData.value.id },
      });
    }, 1000);
  } catch (error) {
    console.error("Error saving activity for preview:", error);
    notify.error(
      "Failed to save activity for preview. Please try again.",
      "Error"
    );
    showValidationErrors.value = true;
    validationErrors.value = {
      api: "Failed to save activity for preview. Please try again.",
    };
  } finally {
    isSubmitting.value = false;
    savingType.value = null;
  }
}

// Format categories for API
const formatCategories = (tagSections) => {
  if (!tagSections || !tagSections.length) return [];

  return tagSections
    .filter(
      (section) =>
        section.id !== "standards" &&
        section.selected &&
        section.selected.length
    )
    .map((section) => {
      const termIds = section.selected
        .map((tag) => {
          try {
            const parsed = typeof tag === "string" ? JSON.parse(tag) : tag;
            return parsed.id;
          } catch (e) {
            return null;
          }
        })
        .filter((id) => id !== null);

      return {
        category_id: section.id,
        term_id: termIds,
      };
    });
};

// Validate activity data
const validateActivityData = () => {
  const errors = {};

  if (!activityData.value.title.trim()) {
    errors.title = "Title is required";
  }

  if (!activityData.value.duration) {
    errors.duration = "Duration is required";
  }

  if (!activityData.value.coverImage) {
    errors.coverImage = "Cover image is required";
  }

  // Add validation for specific required fields
  if (!activityData.value.grade || activityData.value.grade.trim() === "") {
    errors.grade = "Grade is required";
  }

  if (!activityData.value.focus) {
    errors.focus = "Focus is required";
  }

  if (!activityData.value.airtable_id) {
    errors.airtable_id = "Lesson Materials is required";
  }

  if (
    !activityData.value.locale ||
    (Array.isArray(activityData.value.locale) &&
      activityData.value.locale.length === 0)
  ) {
    errors.locale = "Locale is required";
  }

  // Add validation for specific required tag sections
  const requiredSectionTitles = ["Tech Domain", "Topic", "Software", "Grade"];

  const missingSections = activityData.value.tagSections
    .filter(
      (section) =>
        requiredSectionTitles.includes(section.title) &&
        (!section.selected || section.selected.length === 0)
    )
    .map((section) => section.title);

  if (missingSections.length > 0) {
    errors.tagSections = `Please select at least one tag in each of these required categories: ${missingSections.join(
      ", "
    )}`;
  }

  validationErrors.value = errors;

  // Show errors UI if there are errors
  if (Object.keys(errors).length > 0) {
    showValidationErrors.value = true;
  }

  return Object.keys(errors).length === 0;
};

// Save activity (refactored)
const saveActivity = async (status) => {
  if (isSubmitting.value) return;

  // Validate data
  if (!validateActivityData()) {
    return;
  }

  try {
    isSubmitting.value = true;
    savingType.value = status;
    validationErrors.value = {};
    showValidationErrors.value = false;

    // Prepare and save data with the specified status
    const { formData, slideData } = await prepareActivityData(status);
    await saveActivityData(formData, slideData);
    await deleteRemovedMaterials();

    const successMessage = `Lesson materials ${
      status === "published" ? "published" : "saved as draft"
    } successfully!`;

    notify.success(successMessage, "Success");

    // Mark as saved after successful save
    markAsSaved();

    router.push({ name: "LessonMaterialsListPage" });
  } catch (error) {
    console.error("Error updating lesson materials:", error.response);
    const errorMessage =
      error?.response?.data?.message ||
      "Failed to update lesson materials. Please try again.";

    notify.error(errorMessage, "Error");

    showValidationErrors.value = true;
    validationErrors.value = {
      api: errorMessage,
    };
  } finally {
    isSubmitting.value = false;
    savingType.value = null;
  }
};
</script>

<style scoped>
.admin-container {
  position: relative;
  min-height: 100vh;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.preview-button,
.draft-button,
.publish-button,
.clear-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.preview-button {
  background-color: var(--primary-blue);
  color: white;
}

.preview-button:hover {
  opacity: 0.8;
}

.draft-button {
  background-color: #6c757d;
  color: white;
}

.draft-button:hover {
  background-color: #5a6268;
}

.publish-button {
  background-color: #28a745;
  color: white;
}

.publish-button:hover {
  background-color: #218838;
}

.clear-button {
  background-color: #e74c3c;
  color: white;
}

.clear-button:hover {
  background-color: #c0392b;
}

/* Button disabled state */
button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Spinner for loading state */
.action-buttons .spinner {
  width: 16px !important;
  height: 16px !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 50% !important;
  border-top-color: #fff !important;
  animation: spin 0.8s linear infinite !important;
  background-color: transparent !important;
  box-shadow: none !important;
  margin: 0 !important;
  display: inline-block !important;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Validation errors */
.validation-errors {
  margin-bottom: 20px;
  border: 1px solid #f8d7da;
  border-radius: 4px;
  background-color: #fff5f6;
  overflow: hidden;
}

.error-header {
  display: flex;
  align-items: center;
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px 15px;
  font-weight: bold;
  gap: 10px;
}

.error-header h3 {
  margin: 0;
  flex-grow: 1;
}

.close-error {
  background: none;
  border: none;
  color: #721c24;
  font-size: var(--fs-big);
  cursor: pointer;
}

.validation-errors ul {
  margin: 0;
  padding: 15px 15px 15px 40px;
  color: #721c24;
}

.validation-errors li {
  margin-bottom: 5px;
}

/* Snackbar Styles */
.snackbar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 300px;
  max-width: 80%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-radius: 4px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: slide-up 0.3s ease-out;
}

.snackbar.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.snackbar.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.snackbar-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  padding: 0;
  margin-left: 15px;
}

@keyframes slide-up {
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .admin-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-buttons {
    margin-top: 10px;
    width: 100%;
    flex-direction: column;
  }

  .attachment-buttons {
    width: 100%;
    margin-bottom: 10px;
  }

  .preview-button,
  .draft-button,
  .publish-button,
  .attachment-button {
    width: 100%;
    justify-content: center;
  }

  .snackbar {
    width: 90%;
    bottom: 10px;
  }
}
</style>
